import { ButtonCus } from '@/components/common/Button';
import { ClockIcon, LocationIcon } from '@/components/icon';
import { ekDomain, ROUTES } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import { FavoriteLocation } from '@/types/location';
import { isPathEk } from '@/utils/routes';
import { X } from 'lucide-react';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

interface LocationItemProps {
    location: FavoriteLocation,
    isFavorite?: boolean,
    isEdit?: boolean,
    onRemove?: () => void
}

export default observer(function LocationCard({ location, isFavorite, isEdit, onRemove }: LocationItemProps) {
    // hooks
    const { t } = useTranslation();
    const { pathname } = useLocation();
    const navigate = useNavigate();

    // store
    const {
        accountStore: { registerFavoriteLocation, getListFavoriteLocationsByStatus },
        modalStore: { hideAllModals }
    } = useStore();

    // functions
    const handleRegisterFavoriteLocation = async () => {
        const res = await flowResult(registerFavoriteLocation({ locationId: location.id }));
        if (res) {
            getListFavoriteLocationsByStatus({ isFavorite: false });
            getListFavoriteLocationsByStatus({ isFavorite: true });
        }
    };

    const handleRedirectToDetail = () => {
        if (isEdit) return;
        hideAllModals();
        navigate(isPathEk(pathname) ?
            (`/${ekDomain}/${ROUTES.locationList.hrefEK}/${ROUTES.locationDetailEnduser.hrefEK}/${location.id}`) :
            (`/${ROUTES.locationList.href}/${ROUTES.locationDetailEnduser.href}/${location.id}`));
    };

    const showTime = (locationDetail: FavoriteLocation): string => {
        return location?.isShow24h ?
            t('words_title.open_24h') :
            `${locationDetail?.openTimeFrom?.split(':').slice(0, 2).join(':') || '--'} - ${locationDetail?.openTimeTo?.split(':').slice(0, 2).join(':') || '--'}`;
    };

    return (
        <div className='p-3 bg-[#fff] h-full w-full border border-border-gray2 rounded-[9.255px] flex flex-col'>
            <div className='relative'>
                <div
                    className='h-[180px] w-full bg-no-repeat bg-center bg-cover bg-background-img rounded-[7.712px]'
                    style={{
                        backgroundImage: `url(${location?.locationImage || ''})`
                    }}
                    onClick={handleRedirectToDetail}
                >
                </div>
                {isEdit && (
                    <button
                        type='button'
                        onClick={onRemove}
                        className='absolute size-7 -right-[1.55rem] -top-[1.55rem] bg-[#ddd] p-1.5 rounded-full opacity-100 ring-offset-background transition-opacity hover:opacity-85 focus:outline-none disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground'
                    >
                        <X className='h-4 w-4 text-black' />
                    </button>
                )}
            </div>

            <div className='flex flex-col justify-between h-auto mt-4 flex-auto'>
                <div className='pb-3 border-b border-border-gray3'>
                    <div
                        onClick={handleRedirectToDetail}
                        className='box-title line-clamp-1 cursor-pointer'
                    >
                        {location?.name || '--'}
                    </div>
                    <div className='flex justify-start items-center gap-[5px] mt-[10px] mb-[5px]'>
                        <LocationIcon className='w-[16px] h-[16px] flex justify-center items-center' />
                        <div className='text-fs-mid14 leading-lh-sma14 text-text-fc-gray font-medium line-clamp-1'>
                            {location?.address || '--'}
                        </div>
                    </div>
                    <div className='flex justify-start items-center gap-[5px]'>
                        <ClockIcon className='w-[16px] h-[16px] flex justify-center items-center' />
                        <div className='text-fs-mid14 leading-lh-sma14 text-text-fc-gray font-medium line-clamp-1'>
                            {showTime(location)}
                        </div>
                    </div>
                </div>
                {(location?.note || isFavorite?.toString()) && (
                    <div className='pt-2'>
                        {location?.note && (
                            <p className='mb-3 text-text-1 line-clamp-1 font-light'>
                                {location?.note || '--'}
                            </p>
                        )}
                        {isFavorite ?
                            (
                                <ButtonCus
                                    type='button'
                                    variant='default'
                                    size='md'
                                    disabled={isEdit}
                                    onClick={handleRedirectToDetail}
                                    className='w-full'
                                >
                                    {t('button.booking_a_car')}
                                </ButtonCus>
                            ) :
                            (
                                <ButtonCus
                                    type='button'
                                    variant='orange2'
                                    size='md'
                                    onClick={handleRegisterFavoriteLocation}
                                    className='w-full'
                                >
                                    {t('button.add')}
                                </ButtonCus>
                            )}

                    </div>
                )}
            </div>
        </div>
    );
});
